import { View, StyleSheet, useWindowDimensions } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { QRCode } from "@/components/QRCode";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image, ImageSource } from "expo-image";
import { useState } from "react";
import * as Linking from "expo-linking";
import Head from "expo-router/head";

export default function KioskShiftScreen() {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const { shift } = useLocalSearchParams<{ shift: string }>();
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));

  const showLogo = width <= 900;
  const clockInUrl = Linking.createURL(`/clock-in/${shift}`);

  return (
    <>
      <Head>
        <title>RockForce Kiosk</title>
        <meta name="description" content="Scan to clock in to your shift" />
      </Head>
      <View style={styles.outerContainer}>
        <View style={styles.content}>
          {/* Logo Section */}
          {showLogo && (
            <View style={styles.logoSection}>
              <Image source={[logo]} style={styles.logo} />
              <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
              <ThemedText style={[styles.logoSubtitle, { color: colors.textSecondary }]}>Shift Check-In</ThemedText>
            </View>
          )}

          {/* QR Card */}
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Clock In to Your Shift</ThemedText>
            <ThemedText style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
              Scan the QR code below with your phone&apos;s camera
            </ThemedText>

            {/* QR Code */}
            <View style={[styles.qrWrapper, { backgroundColor: "#ffffff" }]}>
              <QRCode value={clockInUrl} size={220} color="#000000" backgroundColor="#ffffff" ecl="H" />
            </View>

            {/* Instructions */}
            <View style={styles.instructions}>
              <View style={styles.instructionRow}>
                <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
                  <ThemedText style={styles.stepNumber}>1</ThemedText>
                </View>
                <ThemedText style={[styles.instructionText, { color: colors.text }]}>
                  Open your phone&apos;s camera app
                </ThemedText>
              </View>
              <View style={styles.instructionRow}>
                <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
                  <ThemedText style={styles.stepNumber}>2</ThemedText>
                </View>
                <ThemedText style={[styles.instructionText, { color: colors.text }]}>
                  Point it at the QR code above
                </ThemedText>
              </View>
              <View style={styles.instructionRow}>
                <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
                  <ThemedText style={styles.stepNumber}>3</ThemedText>
                </View>
                <ThemedText style={[styles.instructionText, { color: colors.text }]}>
                  Tap the notification to check in
                </ThemedText>
              </View>
            </View>
          </View>

          {/* Help Card */}
          <View style={[styles.helpCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <MaterialCommunityIcons name="help-circle-outline" size={20} color={colors.textSecondary} />
            <ThemedText style={[styles.helpText, { color: colors.textSecondary }]}>
              Need help? Ask a supervisor for assistance.
            </ThemedText>
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: "center",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 56,
    width: 56,
    marginBottom: 12,
  },
  logoTitle: {
    fontSize: 24,
    fontWeight: "800",
    letterSpacing: 2,
  },
  logoSubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  card: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    alignItems: "center",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
    textAlign: "center",
  },
  cardSubtitle: {
    fontSize: 14,
    marginBottom: 24,
    textAlign: "center",
  },
  qrWrapper: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  instructions: {
    width: "100%",
    gap: 12,
  },
  instructionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumber: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "bold",
  },
  instructionText: {
    fontSize: 15,
    flex: 1,
  },
  helpCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  helpText: {
    fontSize: 14,
    flex: 1,
  },
});
